import { useEffect, useRef, useState } from "react";

export interface UseTweenedNumberOptions {
  /** Defaults to 500 ms (ease-out quart). Reduced motion jumps straight to the value. */
  durationMs?: number;
}

const DEFAULT_DURATION_MS = 500;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function canAnimate(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.requestAnimationFrame === "function" &&
    typeof window.cancelAnimationFrame === "function" &&
    !prefersReducedMotion()
  );
}

function easeOutQuart(progress: number): number {
  return 1 - Math.pow(1 - progress, 4);
}

/**
 * `value`, animated from the previous value when it changes. Null stays null
 * (the caller prints n/a). The first value shows as is; only changes tween,
 * and whole numbers stay whole while they move.
 */
export function useTweenedNumber(
  value: number | null,
  options: UseTweenedNumberOptions = {},
): number | null {
  const { durationMs = DEFAULT_DURATION_MS } = options;

  const [shown, setShown] = useState<number | null>(value);
  const shownRef = useRef<number | null>(value);

  useEffect(() => {
    const from = shownRef.current;
    if (value == null || from == null || from === value || durationMs <= 0 || !canAnimate()) {
      shownRef.current = value;
      setShown(value);
      return;
    }
    const isWhole = Number.isInteger(from) && Number.isInteger(value);
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const progress = Math.min(1, Math.max(0, (now - start) / durationMs));
      const raw = from + (value - from) * easeOutQuart(progress);
      const next = progress >= 1 ? value : isWhole ? Math.round(raw) : raw;
      shownRef.current = next;
      setShown(next);
      if (progress < 1) {
        frame = window.requestAnimationFrame(step);
      }
    };
    frame = window.requestAnimationFrame(step);
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [value, durationMs]);

  if (value == null) {
    return null;
  }
  return shown ?? value;
}
